<template>
  <q-card-section class="column col items-center justify-center gap-lg q-pa-xl">
    <template v-if="isResolvingPanel">
      <q-spinner
        color="brand-dark"
        size="40px"
        aria-hidden="true"
      />
      <p class="text-14 text-secondary">{{ SESSION_PANEL_COPY.inviteSession.loadingLabel }}</p>
    </template>

    <template v-else>
      <div class="onboarding-icon-badge row items-center justify-center">
        <lucide-icon
          :name="view.icon"
          size="40px"
          :stroke-width="2"
          aria-hidden="true"
        />
      </div>

      <div class="column items-center gap-xxl text-center">
        <h1 class="text-28 text-font-semibold">{{ view.headline }}</h1>
        <div
          v-if="view.subline?.length"
          class="onboarding-subline column items-center gap-xs text-16 text-primary"
        >
          <p
            v-for="(line, index) in view.subline"
            :key="index"
            class="q-ma-none"
          >
            {{ line }}
          </p>
        </div>
      </div>
    </template>
  </q-card-section>

  <q-card-actions
    v-if="!isResolvingPanel"
    class="column items-center gap-xs q-px-xl q-pt-none q-pb-md"
    align="center"
  >
    <q-btn
      v-if="view.secondaryCtaLabel"
      flat
      no-caps
      color="secondary"
      class="onboarding-action-btn sm-radius text-14"
      :loading="view.secondarySwitchesAccount && isBusy"
      :disable="isBusy"
      @click="onSecondary"
    >
      {{ view.secondaryCtaLabel }}
    </q-btn>
    <q-btn
      no-caps
      unelevated
      color="brand"
      class="onboarding-action-btn sm-radius text-14 text-font-semibold"
      :loading="!view.secondarySwitchesAccount && isBusy"
      :disable="isBusy"
      @click="onPrimary"
    >
      {{ view.ctaLabel }}
      <lucide-icon
        :name="view.ctaIcon"
        size="18px"
        color="white"
        class="q-ml-xs"
        aria-hidden="true"
      />
    </q-btn>
  </q-card-actions>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { useAuthStore } from 'src/stores/auth'

  import { useInviteVerificationQuery } from 'src/composables/account/query/useInviteVerificationQuery'

  import { ROUTES } from 'src/router/const'
  import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
  import { INVITE_VERIFICATION_REASON, SESSION_PANEL_COPY } from 'src/services/consts/app.const'
  import { sanitizeInviteToken } from 'src/services/helpers/security.helper'
  import { isSameEmail } from 'src/services/helpers/util.helper'

  // Authenticated visitor on /accept-invite. Auth0 skips the login form for a
  // live session, so a mismatch must be surfaced or the invite stays unconsumed.
  // A dead or missing token is invalid — never claimed as membership success.

  const authStore = useAuthStore()
  const route = useRoute()
  const router = useRouter()

  const token = computed(() => sanitizeInviteToken(route.query.token))

  // Developer-approved exception (project-core.mdc): this public invite surface
  // calls the feature query directly rather than through a store.
  const {
    inviteVerification,
    isInviteVerificationLoading,
    isInviteVerificationRefetching,
    refetchInviteVerification,
  } = useInviteVerificationQuery(token)

  const isRedirecting = ref(false)

  // Only the invite check gates the spinner — session email comes from Auth0
  // and is available before /me lands.
  const isResolvingPanel = computed(() => !!token.value && isInviteVerificationLoading.value)

  const isBusy = computed(() => isRedirecting.value || isInviteVerificationRefetching.value)

  const view = computed(() => {
    const copy = SESSION_PANEL_COPY.inviteSession
    // Runtime-only fields — copy blocks supply the rest via spread.
    const defaults = {
      secondaryCtaLabel: null,
      shouldSwitchAccount: false,
      // Invalid: Switch is secondary (Continue stays primary). Mismatch flips that.
      secondarySwitchesAccount: false,
      shouldRetry: false,
    }
    const invitedEmail = inviteVerification.value?.email || ''
    const currentEmail = authStore.auth0User?.email || ''
    // A missing token and a rejected one are the same thing here.
    const hasUsableInvite = !!token.value && !!inviteVerification.value?.valid

    // The backend never judged the token, so the invite may still be perfectly
    // good — this state must not blame the link or send the invitee to their admin.
    if (inviteVerification.value?.reason === INVITE_VERIFICATION_REASON.UNAVAILABLE) {
      return { ...defaults, ...copy.unavailable, shouldRetry: true }
    }

    if (!hasUsableInvite) {
      return {
        ...defaults,
        ...copy.invalid,
        secondarySwitchesAccount: true,
        subline: copy.invalid.subline.map((line) => line.replace('{currentEmail}', currentEmail)),
      }
    }

    if (isSameEmail(currentEmail, invitedEmail)) {
      return {
        ...defaults,
        ...copy.match,
        subline: copy.match.subline.map((line) => line.replace('{invitedEmail}', invitedEmail)),
      }
    }

    return {
      ...defaults,
      ...copy.mismatch,
      subline: copy.mismatch.subline.map((line) =>
        line.replace('{currentEmail}', currentEmail).replace('{invitedEmail}', invitedEmail)
      ),
      shouldSwitchAccount: true,
    }
  })

  function onContinue() {
    router.push({ name: ROUTES.OVERVIEW })
  }

  function onSecondary() {
    if (view.value.secondarySwitchesAccount) {
      return onSwitchAccount()
    }
    onContinue()
  }

  async function onSwitchAccount() {
    isRedirecting.value = true
    try {
      // Live mismatch: keep ?token= so the right account can still accept.
      // Dead/invalid: nothing left to accept — logout into /app (guard → login),
      // same as EmailVerifiedSessionPanel Switch. Replaying the dead token would
      // bounce them back onto "Invitation not valid".
      const returnTo =
        view.value.shouldSwitchAccount && token.value
          ? `/${ROUTES.ACCEPT_INVITE}?token=${encodeURIComponent(token.value)}`
          : `/${ROUTES.APP}`
      await authStore.logout({ returnTo })
    } catch {
      // authStore.logout already reports to PostHog — this is the user-facing half.
      isRedirecting.value = false
      notifyService.error(notifyMsgs.logoutError)
    }
  }

  function onPrimary() {
    if (view.value.shouldSwitchAccount) {
      return onSwitchAccount()
    }
    // verifyInvite never throws, so a retry always resolves into a fresh view state.
    if (view.value.shouldRetry) {
      return refetchInviteVerification()
    }
    onContinue()
  }
</script>
